import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../Store";
import {
  clearCookies,
  getAccessToken,
  setAuthCookies,
} from "services/cookieService";
import { generateRandomPassword } from "utils/passwordGenerator";
import { addToCart, bulkAddToCart, clearCart, fetchCart } from "./cartSlice";
import { toast } from "react-hot-toast";
import { openEnquiryDialogue } from "./enquiryFormSlice";
import { clearLogoState } from "./logosSlice";
import { clearMockupState } from "./mockupSlice";
import { clearCatalogState } from "./catalogSlice";
import { clearUser } from "./userSlice";
import { IAuthState } from "Interfaces/interface.auth";
import { IUser } from "Interfaces/interface.user";
import ecomInterceptor from "interceptors/ecomInterceptor";
import { ICartItem } from "store/types/cartTypes";
import { extractErrorMessage } from "utils/ErrorHandlers";
import { closeModal } from "./authModalSlice";
import { clearChat } from "./aiChatSlice";
import { resetConfigurations } from "./configurationSlice";

const initialState: IAuthState = {
  user: null,
  isAuthenticated: !!getAccessToken(),
  loading: false,
  error: null,
  tokens: {
    NGAT: getAccessToken() || "",
  },
  isAuthLoaded: false,
  forgotPassword: {
    loading: false,
    success: false,
  },
};

export const checkUserExists = createAsyncThunk<
  { exists: boolean },
  { email: string },
  { rejectValue: string }
>("auth/checkUserExists", async ({ email }, { rejectWithValue }) => {
  try {
    await ecomInterceptor.post("/auth/signup", {
      emailAddress: email,
      password: generateRandomPassword(),
      name: "Temp User",
      company: "",
      role: "",
    });
    await ecomInterceptor.post("/auth/logout", { emailAddress: email });
    return { exists: false };
  } catch (error: any) {
    if (error.response?.data?.message?.includes("Email already exists")) {
      return { exists: true };
    }
    const message = extractErrorMessage(error);
    toast.error(message);
    return rejectWithValue(message);
  }
});

export const socialLogin = createAsyncThunk<
  { user: IUser; token: string },
  { accesstoken: string },
  { state: RootState; rejectValue: string }
>(
  "auth/socialLogin",
  async ({ accesstoken }, { rejectWithValue, dispatch }) => {
    try {
      const response = await ecomInterceptor.post("/auth/google", {
        token: accesstoken,
      });

      const { token, userInfo } = response.data.data;

      if (!token || !userInfo?.userId) {
        throw new Error("No token or user ID received");
      }

      const user: IUser = {
        id: userInfo.userId,
        emailAddress: userInfo.emailAddress,
        phoneNumber: userInfo.phoneNumber?.replace(/^\+91/, "") || null,
        name: userInfo.name,
        company: userInfo.company,
        emailVerified: userInfo.emailVerified,
        phoneNumberVerified: userInfo.phoneNumberVerified,
      };

      // persist
      setAuthCookies(token, user.id!);
      dispatch(setUser(user));
      dispatch(closeModal());

      // make sure token was set
      const setToken = getAccessToken();
      if (!setToken) {
        throw new Error("Token not set in cookies");
      }

      const localCartItems: ICartItem[] = JSON.parse(
        localStorage.getItem("cart") || "[]"
      );

      if (localCartItems.length > 0) {
        await dispatch(bulkAddToCart(localCartItems)).unwrap();
        localStorage.removeItem("cart");
      }

      // Handle enquiry intent
      const enquiryIntent = JSON.parse(
        localStorage.getItem("enquiryIntent") || "{}"
      );
      if (enquiryIntent.products && enquiryIntent.quantities) {
        dispatch(
          openEnquiryDialogue({
            products: enquiryIntent.products,
            quantities: enquiryIntent.quantities,
          })
        );
      }

      // Fetch latest cart from server to show synced items immediately
      dispatch(fetchCart({ userId: user.id! }));

      toast.success("Signed in with Google successfully!");
      return { user, token };
    } catch (error: any) {
      console.error("Social login error details:", error);
      const message = extractErrorMessage(error);

      if (error.response?.status === 403) {
        toast.error(
          "Access denied. Please ensure the Google token is valid or contact support."
        );
      } else {
        toast.error(message);
      }

      return rejectWithValue(message);
    }
  }
);

export const signupUser = createAsyncThunk<
  { user: IUser },
  {
    email: string;
    password: string;
    name: string;
    companyName: string;
    phoneNumber?: string;
  },
  { rejectValue: string }
>(
  "auth/signupUser",
  async (
    { email, password, name, companyName, phoneNumber },
    { rejectWithValue }
  ) => {
    try {
      const response = await ecomInterceptor.post("/auth/signup", {
        emailAddress: email,
        password,
        name,
        company: companyName,
        phoneNumber,
        role: "",
      });
      const { message } = response.data.data;
      if (!message.includes("OTP sent to email")) {
        throw new Error(message || "Failed to send OTP");
      }
      const user: IUser = {
        id: "",
        emailAddress: email,
        phoneNumber,
        name,
        company: companyName,
        emailVerified: false,
        phoneNumberVerified: false,
      };
      return { user };
    } catch (error: any) {
      const message = extractErrorMessage(error);
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const loginUser = createAsyncThunk<
  { user: IUser; tokens: { NGAT: string } },
  { username: string; password: string },
  { state: RootState; rejectValue: string }
>(
  "auth/loginUser",
  async ({ username, password }, { rejectWithValue, dispatch }) => {
    try {
      const response = await ecomInterceptor.post("/auth/login", {
        emailAddress: username,
        password,
      });

      const { message, token, userInfo } = response.data.data;

      if (!token) {
        throw new Error(message || "No token received");
      }

      if (!userInfo?.userId) {
        throw new Error("Failed to fetch user details");
      }

      const user: IUser = {
        id: userInfo.userId,
        emailAddress: userInfo.emailAddress,
        phoneNumber: userInfo.phoneNumber?.replace(/^\+91/, ""),
        name: userInfo.name,
        company: userInfo.company,
        emailVerified: userInfo.emailVerified,
        phoneNumberVerified: userInfo.phoneNumberVerified,
      };

      // persist user (depends if it's Redux action or utility)
      setUser(user);

      dispatch(closeModal());
      setAuthCookies(token, user.id!);

      // Sync local cart items to backend
      const localCartItems: ICartItem[] = JSON.parse(
        localStorage.getItem("cart") || "[]"
      );
      if (localCartItems.length > 0) {
        await dispatch(bulkAddToCart(localCartItems)).unwrap();
        localStorage.removeItem("cart");
      }

      // Handle enquiry intent
      const enquiryIntent = JSON.parse(
        localStorage.getItem("enquiryIntent") || "{}"
      );
      if (enquiryIntent.products && enquiryIntent.quantities) {
        dispatch(
          openEnquiryDialogue({
            products: enquiryIntent.products,
            quantities: enquiryIntent.quantities,
          })
        );
      }

      // Fetch latest cart from server to show synced items immediately
      dispatch(fetchCart({ userId: user.id! }));

      return { user, tokens: { NGAT: token } };
    } catch (error: any) {
      const message = extractErrorMessage(error);
      toast.error(message);
      return rejectWithValue(error);
    }
  }
);

export const registerGuestUser = createAsyncThunk<
  { email: string; tempPassword: string },
  { email: string; phoneNumber: string; name: string; companyName: string },
  { rejectValue: { message: string; errorCode?: number } }
>(
  "auth/registerGuestUser",
  async ({ email, phoneNumber, name, companyName }, { rejectWithValue }) => {
    try {
      const tempPassword = generateRandomPassword();
      const response = await ecomInterceptor.post("/auth/signup", {
        emailAddress: email,
        phoneNumber,
        name,
        company: companyName,
        role: "",
        password: tempPassword,
      });

      const { message } = response.data.data;
      if (!message.includes("OTP sent to email")) {
        throw new Error(message || "Failed to send OTP");
      }

      return { email, tempPassword };
    } catch (error: any) {
      const errorCode = error?.response?.status;
      const message = extractErrorMessage(error);
      toast.error(message);
      return rejectWithValue({ message, errorCode });
    }
  }
);

export const verifyOtp = createAsyncThunk<
  { verifiedType: "email" | "phone" },
  { type: "email" | "phone"; value: string; code: string },
  { rejectValue: string }
>("auth/verifyOtp", async ({ type, value, code }, { rejectWithValue }) => {
  try {
    const payload = {
      otpType: type,
      value,
      otp: code,
    };
    await ecomInterceptor.post("/auth/verify-otp", payload);
    toast.success(
      `${type === "email" ? "Email" : "Phone"} verified successfully`
    );
    return { verifiedType: type };
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    return rejectWithValue(message);
  }
});

export const updateUser = createAsyncThunk<
  { user: IUser },
  {
    name: string;
    phoneNumber?: string;
    company?: string;
    emailAddress: string;
  },
  { rejectValue: string }
>("auth/updateUser", async (credentials, { rejectWithValue }) => {
  try {
    const response = await ecomInterceptor.put("/users", {
      name: credentials.name,
      phone: credentials.phoneNumber,
      company: credentials.company,
    });

    const updatedData = response.data.data;

    const user: IUser = {
      name: updatedData?.name || credentials.name,
      phoneNumber: updatedData?.phone || credentials.phoneNumber,
      emailAddress: updatedData?.email || credentials.emailAddress,
      company: updatedData?.company || credentials.company,
    };

    toast.success("Profile updated successfully");
    return { user };
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    return rejectWithValue(message);
  }
});

export const resendVerificationCode = createAsyncThunk<
  void,
  { type: "email" | "phone"; value: string },
  { rejectValue: string }
>(
  "auth/resendVerificationCode",
  async ({ type, value }, { rejectWithValue }) => {
    try {
      await ecomInterceptor.post("/auth/send-otp", {
        otpType: type,
        value,
      });

      toast.success("OTP sent successfully");
      return;
    } catch (error: any) {
      const message = extractErrorMessage(error);
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const loginGuestUser = createAsyncThunk<
  { user: IUser; tokens: { NGAT: string } },
  { email: string; password: string },
  { state: RootState; rejectValue: string }
>(
  "auth/loginGuestUser",
  async ({ email, password }, { rejectWithValue, dispatch }) => {
    try {
      const response = await ecomInterceptor.post("/auth/login", {
        emailAddress: email,
        password,
      });

      const { token, message, userInfo } = response.data.data;

      if (!token) {
        throw new Error(message || "No token received");
      }

      if (!userInfo?.userId) {
        throw new Error("Guest user info missing in response");
      }

      const user: IUser = {
        id: userInfo.userId,
        emailAddress: userInfo.emailAddress,
        phoneNumber: userInfo.phoneNumber?.replace(/^\+91/, ""),
        name: userInfo.name,
        company: userInfo.company,
        emailVerified: userInfo.emailVerified,
        phoneNumberVerified: userInfo.phoneNumberVerified,
      };

      setUser(user);
      setAuthCookies(token, user.id!);

      // Sync local cart items to backend
      const localCartItems: ICartItem[] = JSON.parse(
        localStorage.getItem("cart") || "[]"
      );
      if (localCartItems.length > 0) {
        await Promise.all(
          localCartItems.map((item) => dispatch(addToCart(item)).unwrap())
        );
        localStorage.setItem("cart", JSON.stringify([])); // Clear local cart after sync
      }

      // Handle enquiry intent
      const enquiryIntent = JSON.parse(
        localStorage.getItem("enquiryIntent") || "{}"
      );
      if (enquiryIntent.products && enquiryIntent.quantities) {
        dispatch(
          openEnquiryDialogue({
            products: enquiryIntent.products,
            quantities: enquiryIntent.quantities,
          })
        );
      }

      // Optional: Navigate if required
      const navigate = (window as any).navigate;
      if (navigate && enquiryIntent.fromPath) {
        navigate(enquiryIntent.fromPath);
      }

      // Fetch latest cart from server to show synced items immediately
      dispatch(fetchCart({ userId: user.id! }));

      return { user, tokens: { NGAT: token } };
    } catch (error: any) {
      const message = extractErrorMessage(error);
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const logoutUser = createAsyncThunk<void, void, { rejectValue: string }>(
  "auth/logoutUser",
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const email = state.auth.user?.emailAddress;
      if (email) {
        await ecomInterceptor.post("/auth/logout", { emailAddress: email });
      }
      clearCookies();
      dispatch(clearUser());
      dispatch(clearCart());
      dispatch(clearLogoState());
      dispatch(clearMockupState());
      dispatch(clearCatalogState());
      dispatch(clearChat());
      dispatch(resetConfigurations());
      toast.success("Signed out successfully!");
    } catch (error: any) {
      const message = extractErrorMessage(error);
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const changePassword = createAsyncThunk<
  { success: boolean },
  { oldPassword: string; newPassword: string },
  { rejectValue: string }
>(
  "auth/changePassword",
  async ({ oldPassword, newPassword }, { rejectWithValue }) => {
    try {
      const response = await ecomInterceptor.post("/auth/change-password", {
        oldPassword,
        newPassword,
      });
      toast.success("Password changed successfully");
      return { success: true };
    } catch (error: any) {
      const message = extractErrorMessage(error);
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const forgotPassword = createAsyncThunk<
  { email: string },
  { email: string },
  { rejectValue: string }
>("auth/forgotPassword", async ({ email }, { rejectWithValue }) => {
  try {
    const response = await ecomInterceptor.post("/auth/forgot-password", {
      emailAddress: email,
    });
    const { message } = response.data.data;
    if (!message.includes("OTP sent to email")) {
      throw new Error(message || "Failed to send password reset OTP");
    }
    toast.success("Password reset OTP sent to email");
    return { email };
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    return rejectWithValue(message);
  }
});

export const confirmForgotPassword = createAsyncThunk<
  { email: string },
  { email: string; code: string; newPassword: string },
  { rejectValue: string }
>(
  "auth/resetPassword",
  async ({ email, code, newPassword }, { rejectWithValue }) => {
    try {
      const response = await ecomInterceptor.post("/auth/reset-password", {
        emailAddress: email,
        newPassword,
        otp: code,
      });
      const { message } = response.data.data;
      toast.success(message || "Password reset successfully");
      return { email };
    } catch (error: any) {
      const message = extractErrorMessage(error);
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<IUser | null>) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      state.isAuthLoaded = true;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetForgotPasswordState: (state) => {
      state.forgotPassword = {
        loading: false,
        success: false,
      };
    },
    clearAuthState: (state) => {
      state.user = null;
      state.loading = false;
      state.error = null;
      state.isAuthenticated = false;
      state.tokens = { NGAT: "" };
      state.isAuthLoaded = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkUserExists.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkUserExists.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(checkUserExists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to check user existence";
      })
      .addCase(signupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = false;
        state.isAuthLoaded = true;
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to sign up";
      })
      .addCase(registerGuestUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerGuestUser.fulfilled, (state) => {
        state.loading = false;
        state.isAuthLoaded = true;
      })
      .addCase(registerGuestUser.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to register guest user";
      })
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        if (state.user) {
          if (action.payload.verifiedType === "email") {
            state.user.emailVerified = true;
          } else if (action.payload.verifiedType === "phone") {
            state.user.phoneNumberVerified = true;
          }
        }
        state.isAuthLoaded = false;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "OTP verification failed";
      })
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthLoaded = false;
      })
      .addCase(updateUser.rejected, (state) => {
        state.loading = false;
      })
      .addCase(loginGuestUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginGuestUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.tokens = {
          NGAT: action.payload.tokens.NGAT,
        };
        state.isAuthLoaded = true;
      })
      .addCase(loginGuestUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Guest login failed";
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.tokens = {
          NGAT: action.payload.tokens.NGAT,
        };
        state.isAuthLoaded = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Login failed";
      })
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.tokens = { NGAT: "" };
        state.isAuthLoaded = false;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Logout failed";
      })
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to change password";
      })
      .addCase(forgotPassword.pending, (state) => {
        state.forgotPassword.loading = true;
        state.forgotPassword.success = false;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.forgotPassword.loading = false;
        state.forgotPassword.success = true;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.forgotPassword.loading = false;
        state.error = action.payload || "Failed to initiate password reset";
      })
      .addCase(confirmForgotPassword.pending, (state) => {
        state.forgotPassword.loading = true;
        state.forgotPassword.success = false;
        state.error = null;
      })
      .addCase(confirmForgotPassword.fulfilled, (state) => {
        state.forgotPassword.loading = false;
        state.forgotPassword.success = true;
      })
      .addCase(confirmForgotPassword.rejected, (state, action) => {
        state.forgotPassword.loading = false;
        state.error = action.payload || "Failed to reset password";
      })
      .addCase(resendVerificationCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendVerificationCode.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resendVerificationCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to resend OTP";
      })
      .addCase(socialLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(socialLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.tokens = { NGAT: action.payload.token };
      })
      .addCase(socialLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setUser, clearError, resetForgotPasswordState, clearAuthState } =
  authSlice.actions;
export default authSlice.reducer;

export const selectAuth = (state: RootState) => state.auth;
export const selectUser = (state: RootState) => state.auth.user;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectIsAuthenticated = (state: RootState) =>
  state.auth.isAuthenticated;
export const selectAuthLoading = (state: RootState) => state.auth.loading;
export const selectIsAuthLoaded = (state: RootState) => state.auth.isAuthLoaded;
export const selectForgotPassword = (state: RootState) =>
  state.auth.forgotPassword;
